import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, ArrowRight, Check, ChevronDown, Compass, Pause, RotateCcw, SkipForward } from "lucide-react";
import { findLiveTarget, type LiveTourStep } from "@/lib/liveProductTour";
import type { DemoRequestEvent } from "@/stores/demoWorkspace";

interface Props {
  chapterId: string;
  chapterTitle: string;
  step: LiveTourStep;
  index: number;
  total: number;
  ownerId: string;
  syncError: boolean;
  onNext: () => void;
  onBack: () => void;
  onPause: () => void;
  onSkip: () => void;
  onReturn: () => void;
  onRestart: () => void;
}
type Box = { x: number; y: number; width: number; height: number };
type Frame = { box: Box | null; width: number; height: number; top: number; bannerBottom: number };

/** The hole follows the real DOM element. Page controls remain interactive. */
export function LiveTourSpotlight(props: Props) {
  const { step, index, total, ownerId } = props;
  const [frame, setFrame] = useState<Frame>({ box: null, width: window.innerWidth, height: window.innerHeight, top: 0, bannerBottom: 0 });
  const [targetReady, setTargetReady] = useState(false);
  const [passed, setPassed] = useState(false);
  const [collapsed, setCollapsed] = useState(window.innerWidth < 768 && step.completion === "request");
  const [tucked, setTucked] = useState(false);
  const [cardHeight, setCardHeight] = useState(300);
  const cardRef = useRef<HTMLElement>(null);
  const interacted = useRef(false);
  const targetRef = useRef<HTMLElement | undefined>(undefined);
  const confirmationRef = useRef<HTMLElement | undefined>(undefined);
  const onNextRef = useRef(props.onNext);
  useLayoutEffect(() => { onNextRef.current = props.onNext; });

  useLayoutEffect(() => {
    let alive = true;
    let animation = 0;
    let advanceTimer: ReturnType<typeof setTimeout> | undefined;
    let scrolled = false;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const card = cardRef.current;
    const watched = new ResizeObserver(() => schedule());
    function measure() {
      animation = 0;
      if (!alive) return;
      const target = findLiveTarget(step);
      // A portaled confirmation belongs to the current form even though it
      // sits outside the drawer in the DOM. Keep its native controls in focus.
      const confirmation = step.completion === "request"
        ? findLiveTarget({ ...step, targets: ['.modal--open[role="alertdialog"]'] })
        : undefined;
      const previousConfirmation = confirmationRef.current;
      confirmationRef.current = confirmation;
      if (targetRef.current !== target) {
        targetRef.current?.removeAttribute("data-live-tour-target");
        watched.disconnect();
        targetRef.current = target;
        if (target) { watched.observe(target); target.setAttribute("data-live-tour-target", props.chapterId); }
      }
      if (previousConfirmation !== confirmation) {
        if (previousConfirmation) watched.unobserve(previousConfirmation);
        if (confirmation) watched.observe(confirmation);
      }
      setTargetReady(!!target);
      const viewport = window.visualViewport;
      const width = viewport?.width ?? window.innerWidth;
      const height = viewport?.height ?? window.innerHeight;
      const top = viewport?.offsetTop ?? 0;
      const bannerBottom = document.querySelector(".demo-workspace-banner")?.getBoundingClientRect().bottom ?? 0;
      let box: Box | null = null;
      if (target) {
        if (!scrolled) {
          scrolled = true;
          target.scrollIntoView({ block: "center", inline: "nearest", behavior: "instant" });
          if (width < 768 && target.getBoundingClientRect().height > height * .45) setCollapsed(true);
        }
        const rect = (confirmation ?? target).getBoundingClientRect();
        const x = Math.max(4, rect.left - 6);
        const y = Math.max(top + 4, rect.top - 6);
        box = { x, y, width: Math.max(0, Math.min(width - 4, rect.right + 6) - x), height: Math.max(0, Math.min(top + height - 4, rect.bottom + 6) - y) };
        if (box.width < 2 || box.height < 2) box = null;
        // Transform transitions move a native drawer/modal without resizing
        // its layout box. Follow those frames until its own animation settles.
        if ((confirmation ?? target).getAnimations().some((animation) => animation.playState === "running")) schedule();
      }
      setFrame((old) => JSON.stringify(old) === JSON.stringify({ box, width, height, top, bannerBottom }) ? old : { box, width, height, top, bannerBottom });
    }
    function schedule() { if (!animation) animation = requestAnimationFrame(measure); }
    function interaction(event: Event) {
      const target = targetRef.current;
      if (!target || !(event.target instanceof Node) || (!target.contains(event.target) && !confirmationRef.current?.contains(event.target))) return;
      if (event.type === (step.activation ?? "click") && step.completion === "click") {
        if (event.target instanceof HTMLElement && event.target.closest('[disabled], [aria-disabled="true"]')) return;
        interacted.current = true;
        setPassed(true);
        if (!advanceTimer) advanceTimer = setTimeout(() => { if (alive) onNextRef.current(); }, 180);
      } else if ((event.type === "change" || event.type === "input") && ["change", "input"].includes(step.completion)) {
        interacted.current = true;
        setPassed(true);
      } else if (step.completion === "request") {
        interacted.current = true;
        if (window.innerWidth < 768) setTucked(true);
      }
    }
    function request(event: Event) {
      const detail = (event as CustomEvent<DemoRequestEvent>).detail;
      if (!detail || !detail.demo || detail.ownerId !== ownerId || !interacted.current || step.completion !== "request" || !step.request) return;
      if (detail.method.toUpperCase() === step.request.method.toUpperCase() && step.request.path.test(detail.path)) setPassed(true);
    }
    const mutations = new MutationObserver(schedule);
    mutations.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["class", "style", "hidden", "aria-expanded", "disabled"] });
    window.addEventListener("resize", schedule);
    document.addEventListener("scroll", schedule, true);
    window.visualViewport?.addEventListener("resize", schedule);
    window.visualViewport?.addEventListener("scroll", schedule);
    for (const type of ["click", "pointerdown", "input", "change"]) document.addEventListener(type, interaction, true);
    window.addEventListener("trace:tour-request", request);
    measure();
    return () => {
      alive = false;
      if (animation) cancelAnimationFrame(animation);
      if (advanceTimer) clearTimeout(advanceTimer);
      mutations.disconnect(); watched.disconnect();
      targetRef.current?.removeAttribute("data-live-tour-target");
      targetRef.current = undefined;
      confirmationRef.current = undefined;
      window.removeEventListener("resize", schedule);
      document.removeEventListener("scroll", schedule, true);
      window.visualViewport?.removeEventListener("resize", schedule);
      window.visualViewport?.removeEventListener("scroll", schedule);
      for (const type of ["click", "pointerdown", "input", "change"]) document.removeEventListener(type, interaction, true);
      window.removeEventListener("trace:tour-request", request);
      if (previousFocus?.isConnected && document.activeElement === card) previousFocus.focus({ preventScroll: true });
    };
  }, [step, ownerId, props.chapterId]);

  // Measure the current instructions before paint. A step can change from a
  // full card to compact instructions during the same layout pass; waiting
  // for ResizeObserver would briefly place Continue using the old height.
  useLayoutEffect(() => {
    const height = cardRef.current?.getBoundingClientRect().height;
    if (height !== undefined) setCardHeight((previous) => previous === height ? previous : height);
  }, [collapsed, tucked, passed, targetReady, step, props.syncError, props.chapterTitle, index, total, frame.width, frame.height, frame.box]);

  useLayoutEffect(() => {
    if (!cardRef.current) return;
    const observer = new ResizeObserver(([entry]) => { if (entry) setCardHeight(entry.target.getBoundingClientRect().height); });
    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);
  const pause = props.onPause;
  useEffect(() => {
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented && !document.querySelector('[role="dialog"]:not(.live-tour-card)')) pause();
    };
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [pause]);

  const continueAllowed = passed || (step.completion === "inspect" && targetReady && !!frame.box);
  const advance = useCallback(() => { if (continueAllowed) props.onNext(); }, [continueAllowed, props]);
  const { box, width, height, top, bannerBottom } = frame;
  const cardWidth = Math.min(collapsed && width < 768 ? 300 : 350, width - 24);
  let left = Math.max(12, width - cardWidth - 20);
  let cardTop = top + 16;
  // When navigating away from a feature, keep the fallback instructions
  // below the workspace banner so its exit and reset controls stay usable.
  if (!box && bannerBottom > top && bannerBottom < top + height) cardTop = bannerBottom + 12;
  if (box) {
    if (width > 760 && box.x + box.width + cardWidth + 28 < width) { left = box.x + box.width + 16; cardTop = Math.max(top + 16, box.y); }
    else if (width > 760 && box.x > cardWidth + 28) { left = box.x - cardWidth - 16; cardTop = Math.max(top + 16, box.y); }
    else {
      left = Math.min(width - cardWidth - 12, Math.max(12, box.x));
      const below = box.y + box.height + 16;
      cardTop = below + cardHeight < top + height - 24 ? below : box.y - cardHeight - 16;
      if (cardTop < top + 12) cardTop = top + height - cardHeight - (width < 768 ? 84 : 18);
    }
  }
  // A phone form fills the viewport: its upper title is a safer dock than
  // the submit controls. Instructions tuck away as the user edits the form.
  if (collapsed && width < 768 && step.completion === "request" && box && box.height > height * .45) cardTop = top + 12;
  cardTop = Math.max(top + 12, Math.min(cardTop, top + height - cardHeight - 12));
  const maskId = `tour-hole-${props.chapterId}-${index}`;
  return createPortal(<>
    <svg className="live-tour-spotlight" aria-hidden="true" width={width} height={top + height}>
      <defs><mask id={maskId}><rect width="100%" height="100%" fill="white" />{box && <rect x={box.x} y={box.y} width={box.width} height={box.height} rx="8" fill="black" />}</mask></defs>
      <rect width="100%" height="100%" fill="rgba(8, 12, 18, .44)" mask={`url(#${maskId})`} />
      {box && <rect x={box.x} y={box.y} width={box.width} height={box.height} rx="8" fill="none" stroke="var(--tr-accent)" strokeWidth="2" />}
    </svg>
    <section ref={cardRef} className={`live-tour-card ${collapsed ? "live-tour-card--collapsed" : ""}`} style={{ left, top: cardTop, width: cardWidth, maxHeight: height - 24 }} role="dialog" aria-modal="false" aria-label="Guided feature tour" data-chapter={props.chapterId} data-step={index} data-step-id={step.id} data-completion={step.completion} data-target-ready={targetReady} data-passed={passed}>
      <header><Compass size={15} /><span>{props.chapterTitle}</span><span>{index + 1}/{total}</span><button aria-label={collapsed ? "Expand guide" : "Minimize guide"} onClick={() => { setTucked(false); setCollapsed(!collapsed); }}><ChevronDown size={16} /></button></header>
      {collapsed && (!tucked || continueAllowed) && <div className="live-tour-card__compact"><strong>{step.title}</strong><p>{step.body}</p>{continueAllowed ? <button className="btn btn--accent btn--sm" onClick={advance}>{index === total - 1 ? "Next feature" : "Continue"}<ArrowRight size={13} /></button> : <button className="live-tour-card__hide" onClick={() => setTucked(true)}>Hide instructions</button>}</div>}
      {!collapsed && <>
        <div className="live-tour-card__body"><span className="tour-eyebrow">REAL WORKSPACE / DEMO DATA</span><div aria-live="polite" aria-atomic="true"><h2>{step.title}</h2><p>{step.body}</p></div>
          {!targetReady && !passed && <div className="live-tour-card__missing" role="status">Waiting for this control. Reopen the previous control if its panel was closed, or restart this feature.<div>{index > 0 && <button onClick={props.onBack}>Reopen previous control</button>}<button onClick={props.onReturn}>Return to feature</button><button onClick={props.onRestart}><RotateCcw size={12} />Restart steps</button></div></div>}
          {targetReady && !continueAllowed && <p className="live-tour-card__hint" role="status">{step.completion === "click" ? "Click the highlighted control to continue." : step.completion === "request" ? "Use the highlighted form and save your change." : "Change the highlighted control, then continue."}</p>}
          {passed && <p className="live-tour-card__verified" role="status"><Check size={13} />Action completed in the demo workspace</p>}
          {props.syncError && <p className="tour-muted">Progress saved on this device. Account sync will retry when connected.</p>}
        </div>
        <div className="live-tour-card__navigation"><button className="btn btn--ghost btn--sm" onClick={props.onBack} disabled={index === 0} aria-label="Previous step"><ArrowLeft size={14} /></button><button className="btn btn--accent btn--sm" onClick={advance} disabled={!continueAllowed}>{index === total - 1 ? "Next feature" : "Continue"}<ArrowRight size={14} /></button></div>
        <footer><button onClick={props.onPause}><Pause size={12} />Pause tour</button><button onClick={props.onSkip}><SkipForward size={12} />Skip feature</button></footer>
      </>}
    </section>
  </>, document.body);
}
